/**
 * ResourceExtractor - Extract embedded resources from chapter HTML
 *
 * Parses HTML to find images, CSS, and fonts that need to be downloaded
 * for offline reading.
 */

export interface ExtractedResource {
  url: string;
  type: 'image' | 'css' | 'font' | 'other';
  tagType: string; // 'img', 'link', 'style', etc.
  attribute: string; // 'src', 'href', etc.
}

export class ResourceExtractor {
  /**
   * Extract all resources from chapter HTML
   */
  extractResources(html: string, baseUrl: string): ExtractedResource[] {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const resources: ExtractedResource[] = [];

    // Extract images (<img src="...">)
    const images = doc.querySelectorAll('img[src]');
    images.forEach((img) => {
      const src = img.getAttribute('src');
      console.log(`  Found <img src="${src}">`);
      if (src && this.isRelativeOrDataUrl(src)) {
        const resolvedUrl = this.resolveUrl(src, baseUrl);
        console.log(`    → Resolved to: ${resolvedUrl}`);
        resources.push({
          url: resolvedUrl,
          type: 'image',
          tagType: 'img',
          attribute: 'src',
        });
      } else {
        console.log(`    → Skipped (external or data URL)`);
      }
    });

    // Extract CSS (<link rel="stylesheet" href="...">)
    const stylesheets = doc.querySelectorAll('link[rel="stylesheet"][href]');
    stylesheets.forEach((link) => {
      const href = link.getAttribute('href');
      if (href && this.isRelativeOrDataUrl(href)) {
        resources.push({
          url: this.resolveUrl(href, baseUrl),
          type: 'css',
          tagType: 'link',
          attribute: 'href',
        });
      }
    });

    // Extract inline styles with url()
    const styleElements = doc.querySelectorAll('style');
    styleElements.forEach((style) => {
      const cssText = style.textContent || '';
      const urlMatches = cssText.matchAll(/url\(['"]?([^'"()]+)['"]?\)/g);

      for (const match of urlMatches) {
        const url = match[1];
        if (url && this.isRelativeOrDataUrl(url)) {
          const resourceType = this.detectResourceType(url);
          resources.push({
            url: this.resolveUrl(url, baseUrl),
            type: resourceType,
            tagType: 'style',
            attribute: 'url()',
          });
        }
      }

      // Extract fonts (@font-face in this style block)
      const fontFaces = cssText.matchAll(/@font-face\s*{[^}]*src:\s*url\(['"]?([^'"()]+)['"]?\)/g);
      for (const match of fontFaces) {
        const url = match[1];
        if (url && this.isRelativeOrDataUrl(url)) {
          resources.push({
            url: this.resolveUrl(url, baseUrl),
            type: 'font',
            tagType: 'style',
            attribute: 'url()',
          });
        }
      }
    });

    // Deduplicate resources by URL
    const uniqueResources = Array.from(
      new Map(resources.map(r => [r.url, r])).values()
    );

    console.log(`📦 Extracted ${uniqueResources.length} resources from HTML`);
    return uniqueResources;
  }

  /**
   * Check if URL is relative or data URL (not absolute external URL)
   */
  private isRelativeOrDataUrl(url: string): boolean {
    // Skip data URLs (already embedded)
    if (url.startsWith('data:')) {
      return false;
    }

    // Skip absolute external URLs (http://, https://)
    if (url.match(/^https?:\/\//)) {
      return false;
    }

    // Relative URLs or absolute paths
    return true;
  }

  /**
   * Resolve relative URL to absolute URL
   * IMPORTANT: Keep API paths as-is for Vite proxy to handle
   */
  private resolveUrl(url: string, baseUrl: string): string {
    try {
      // API paths (/api/...) should stay as-is for Vite proxy
      if (url.startsWith('/api/')) {
        return url;
      }

      // If URL is already absolute path or URL, return as-is
      if (url.startsWith('/') || url.match(/^https?:\/\//)) {
        return url;
      }

      // Resolve relative URL
      const base = new URL(baseUrl);
      const resolved = new URL(url, base);
      return resolved.href;
    } catch (err) {
      console.warn(`Failed to resolve URL: ${url}`, err);
      return url;
    }
  }

  /**
   * Detect resource type from URL/extension
   */
  private detectResourceType(url: string): 'image' | 'css' | 'font' | 'other' {
    const ext = url.split('.').pop()?.toLowerCase() || '';

    if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp'].includes(ext)) {
      return 'image';
    }

    if (['css'].includes(ext)) {
      return 'css';
    }

    if (['woff', 'woff2', 'ttf', 'otf', 'eot'].includes(ext)) {
      return 'font';
    }

    return 'other';
  }

  /**
   * Detect MIME type from URL/extension
   */
  getMimeType(url: string): string {
    const ext = url.split('.').pop()?.toLowerCase() || '';

    const mimeTypes: Record<string, string> = {
      // Images
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      gif: 'image/gif',
      svg: 'image/svg+xml',
      webp: 'image/webp',
      bmp: 'image/bmp',

      // CSS
      css: 'text/css',

      // Fonts
      woff: 'font/woff',
      woff2: 'font/woff2',
      ttf: 'font/ttf',
      otf: 'font/otf',
      eot: 'application/vnd.ms-fontobject',
    };

    return mimeTypes[ext] || 'application/octet-stream';
  }

  /**
   * Replace resource URLs in HTML with new URLs (blob URLs or data URLs)
   */
  replaceResourceUrls(html: string, urlMap: Map<string, string>): string {
    let modifiedHtml = html;

    console.log('🔄 URL replacement - checking', urlMap.size, 'resources');
    console.log('📄 HTML length:', html.length, 'characters');

    urlMap.forEach((newUrl, originalUrl) => {
      console.log(`\n  🔍 Trying to replace: "${originalUrl}"`);
      console.log(`     → New blob URL: "${newUrl.substring(0, 50)}..."`);

      // First, let's see if this URL appears anywhere in the HTML at all
      const urlInHtml = html.includes(originalUrl);
      console.log(`     → URL exists in HTML: ${urlInHtml ? '✅ YES' : '❌ NO'}`);

      if (!urlInHtml) {
        // Try to find what's actually in the HTML
        // Extract the filename from the URL
        const urlParts = originalUrl.split('/');
        const filenameWithQuery = urlParts[urlParts.length - 1];
        const filename = filenameWithQuery.split('?')[0];

        console.log(`     → Looking for filename in HTML: "${filename}"`);

        // Find all img/link tags in the HTML
        const imgMatches = html.match(/src=["'][^"']*["']/g) || [];
        const linkMatches = html.match(/href=["'][^"']*["']/g) || [];

        console.log(`     → Found ${imgMatches.length} src attributes, ${linkMatches.length} href attributes`);

        // Show first few matches for debugging
        if (imgMatches.length > 0) {
          console.log(`     → Sample src attributes:`);
          imgMatches.slice(0, 3).forEach((match, idx) => {
            console.log(`        ${idx + 1}. ${match}`);
          });
        }
      }

      let replacementCount = 0;

      // Try 1: Simple string replacement (most reliable)
      if (modifiedHtml.includes(originalUrl)) {
        const beforeReplace = modifiedHtml;
        modifiedHtml = modifiedHtml.split(originalUrl).join(newUrl);
        if (modifiedHtml !== beforeReplace) {
          const count = beforeReplace.split(originalUrl).length - 1;
          replacementCount += count;
          console.log(`     ✅ Replaced ${count} occurrence(s) via string replacement`);
        }
      }

      if (replacementCount === 0) {
        console.log(`     ⚠️ No replacements made - URL format mismatch`);
      }
    });

    console.log(`\n📊 Replacement summary: Modified ${urlMap.size} URLs\n`);
    return modifiedHtml;
  }

  /**
   * Escape special regex characters
   */
  private escapeRegex(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
}

// Singleton instance
export const resourceExtractor = new ResourceExtractor();
